"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Play } from "lucide-react";
import {
  resolveDemoVideoPosterUrl,
  resolveDemoVideoUrl,
} from "@/lib/media/demo-video-url";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

type ExampleVideo = {
  title: string;
  metric: string;
  iconSrc: string;
  poster: string;
  src: string;
};

function demoVideo(fileName: string) {
  return {
    poster: resolveDemoVideoPosterUrl(fileName),
    src: resolveDemoVideoUrl(fileName),
  };
}

const exampleVideos: ExampleVideo[] = [
  {
    title: "Doodle Comic",
    metric: "128K views",
    iconSrc: "/images/hero-carousel-template-icons/messages.png",
    ...demoVideo("stickman.mp4"),
  },
  {
    title: "Reddit Story",
    metric: "282K views",
    iconSrc: "/images/hero-carousel-template-icons/reddit.png",
    ...demoVideo("reddit-comments.mp4"),
  },
  {
    title: "Pixar Story",
    metric: "1.2M views",
    iconSrc: "/images/hero-carousel-template-icons/history.png",
    ...demoVideo("pixar.mp4"),
  },
  {
    title: "Text Messages",
    metric: "144K views",
    iconSrc: "/images/hero-carousel-template-icons/messages.png",
    ...demoVideo("text-messages.mp4"),
  },
  {
    title: "Famous Mystery",
    metric: "32K views",
    iconSrc: "/images/hero-carousel-template-icons/history.png",
    ...demoVideo("mary-celeste.mp4"),
  },
  {
    title: "Motivation",
    metric: "114K views",
    iconSrc: "/images/hero-carousel-template-icons/motivation.png",
    ...demoVideo("motivation.mp4"),
  },
  {
    title: "Reddit Comments",
    metric: "418K views",
    iconSrc: "/images/hero-carousel-template-icons/reddit.png",
    ...demoVideo("reddit-comments-example.mp4"),
  },
  {
    title: "Dark Comic",
    metric: "+14,920 followers",
    iconSrc: "/images/hero-carousel-template-icons/horror.png",
    ...demoVideo("dark-comic.mp4"),
  },
  {
    title: "Doodle Comic",
    metric: "862K views",
    iconSrc: "/images/hero-carousel-template-icons/messages.png",
    ...demoVideo("stickman-volcano-example.optimized.mp4"),
  },
  {
    title: "History Facts",
    metric: "+3460 followers",
    iconSrc: "/images/hero-carousel-template-icons/history.png",
    ...demoVideo("history.mp4"),
  },
  {
    title: "Confession",
    metric: "1.1M views",
    iconSrc: "/images/hero-carousel-template-icons/confession.png",
    ...demoVideo("confession.mp4"),
  },
  {
    title: "Mystery Story",
    metric: "20K views",
    iconSrc: "/images/hero-carousel-template-icons/history.png",
    ...demoVideo("underwater-mystery.mp4"),
  },
  {
    title: "Text Message Drama",
    metric: "731K views",
    iconSrc: "/images/hero-carousel-template-icons/messages.png",
    ...demoVideo("text-messages-example.mp4"),
  },
  {
    title: "Mystery Story",
    metric: "960K views",
    iconSrc: "/images/hero-carousel-template-icons/horror.png",
    ...demoVideo("mystery.mp4"),
  },
  {
    title: "Documentary",
    metric: "92K views",
    iconSrc: "/images/hero-carousel-template-icons/history.png",
    ...demoVideo("documentary.mp4"),
  },
];

function ExampleVideoCard({
  video,
  onSelect,
}: {
  video: ExampleVideo;
  onSelect: (video: ExampleVideo) => void;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);

  useEffect(() => {
    const element = buttonRef.current;

    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      const frameId = window.requestAnimationFrame(() => {
        setShouldLoadVideo(true);
      });
      return () => window.cancelAnimationFrame(frameId);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoadVideo(true);
          observer.disconnect();
        }
      },
      { rootMargin: "520px 0px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoadVideo) {
      return;
    }

    const element = videoRef.current;
    if (!element) {
      return;
    }

    const playPromise = element.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        // Ignore stricter autoplay settings; the modal player remains user-initiated.
      });
    }
  }, [shouldLoadVideo]);

  return (
    <button
      ref={buttonRef}
      aria-label={`Play ${video.title} example with sound`}
      className="group/card relative h-[340px] w-[191px] shrink-0 overflow-hidden rounded-[20px] border border-white/10 bg-black/35 shadow-[0_24px_70px_rgba(0,0,0,0.34)] outline-none transition duration-300 hover:-translate-y-1 hover:border-fuchsia-300/42 hover:shadow-[0_26px_90px_rgba(168,85,247,0.2)] focus-visible:ring-2 focus-visible:ring-fuchsia-300/70 md:h-[420px] md:w-[236px]"
      onClick={() => onSelect(video)}
      type="button"
    >
      <img
        alt=""
        aria-hidden="true"
        className={[
          "absolute inset-0 size-full object-cover transition duration-500 group-hover/card:scale-[1.035]",
          isVideoReady ? "opacity-0" : "opacity-100",
        ].join(" ")}
        decoding="async"
        src={video.poster}
      />
      <video
        ref={videoRef}
        className="absolute inset-0 size-full object-cover transition duration-500 group-hover/card:scale-[1.035]"
        src={shouldLoadVideo ? video.src : undefined}
        autoPlay={shouldLoadVideo}
        loop
        muted
        onLoadedData={() => setIsVideoReady(true)}
        onPlaying={() => setIsVideoReady(true)}
        playsInline
        preload={shouldLoadVideo ? "metadata" : "none"}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/18 opacity-80 transition group-hover/card:opacity-95" />
      <div className="absolute inset-x-3 top-3 flex items-center rounded-full border border-white/10 bg-black/28 px-2.5 py-1 text-white/90 backdrop-blur-md md:px-3">
        <span className="flex min-w-0 items-center gap-2 text-[0.8125rem] font-bold leading-none md:text-[0.9375rem]">
          <Image
            src={video.iconSrc}
            alt=""
            aria-hidden="true"
            className="size-6 shrink-0 rounded-md object-cover md:size-7"
            loading="lazy"
            width={28}
            height={28}
          />
          <span className="truncate">{video.title}</span>
        </span>
      </div>
      <div className="absolute inset-0 flex items-center justify-center opacity-0 transition duration-300 group-hover/card:opacity-100 group-focus-visible/card:opacity-100">
        <span className="flex size-14 items-center justify-center rounded-full border border-white/20 bg-white/90 text-slate-950 shadow-[0_18px_44px_rgba(0,0,0,0.35)] backdrop-blur">
          <Play className="ml-0.5 size-5 fill-current" aria-hidden />
        </span>
      </div>
      <div className="absolute inset-x-4 bottom-4 text-left">
        <p className="text-sm font-medium text-white/78 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">
          {video.metric}
        </p>
      </div>
    </button>
  );
}

export function ExampleVideosMarqueeSection() {
  const [selectedVideo, setSelectedVideo] = useState<ExampleVideo | null>(null);
  const marqueeVideos = [...exampleVideos, ...exampleVideos];

  return (
    <section className="overflow-hidden py-16 md:py-20">
      <div className="group relative">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-[18vw] bg-gradient-to-r from-[#111113] to-transparent"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-[18vw] bg-gradient-to-l from-[#111113] to-transparent"
        />
        <div className="flex w-max gap-4 animate-[exampleVideoMarquee_58s_linear_infinite] px-4 [will-change:transform] group-hover:[animation-play-state:paused] motion-reduce:animate-none md:gap-5 md:px-7">
          {marqueeVideos.map((video, index) => (
            <ExampleVideoCard
              key={`${video.src}-${index}`}
              video={video}
              onSelect={setSelectedVideo}
            />
          ))}
        </div>
      </div>

      <Dialog
        open={selectedVideo !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedVideo(null);
          }
        }}
      >
        <DialogContent className="max-w-[min(92vw,520px)] border-white/10 bg-[#08080d] p-3 shadow-[0_34px_120px_rgba(0,0,0,0.72)] sm:p-4">
          <DialogTitle className="sr-only">
            {selectedVideo ? selectedVideo.title : "Example video"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Expanded ReelMagic example video with audio controls.
          </DialogDescription>
          {selectedVideo ? (
            <video
              key={selectedVideo.src}
              className="mx-auto aspect-[9/16] max-h-[82vh] w-full rounded-[22px] bg-black object-contain"
              src={selectedVideo.src}
              autoPlay
              controls
              poster={selectedVideo.poster}
              playsInline
              preload="auto"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}
